import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ThreadEvent {
  id: string;
  thread_id: string | null;
  ticket_id: string | null;
  inbox_thread_id: string | null;
  shop_id: string;
  event_type: string;
  event_source: string;
  channel: string;
  visibility: string;
  content: string;
  payload: Record<string, unknown>;
  author_user_id: string | null;
  author_role: string | null;
  customer_id: string | null;
  is_pinned: boolean;
  parent_event_id: string | null;
  created_at: string;
  // Joined data
  author_name?: string;
}

export interface TicketThread {
  id: string;
  ticket_id: string;
  shop_id: string;
  customer_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface AISummary {
  id: string;
  thread_id: string;
  summary_text: string;
  summary_in_progress: boolean;
  model_used: string | null;
  created_at: string;
  updated_at: string;
}

interface UseTicketThreadResult {
  thread: TicketThread | null;
  events: ThreadEvent[];
  summary: AISummary | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useTicketThread(ticketId: string | null): UseTicketThreadResult {
  // Fetch thread for ticket
  const threadQuery = useQuery({
    queryKey: ['ticket-thread', ticketId],
    queryFn: async () => {
      if (!ticketId) return null;
      
      // R125-M-6: explicit column list matches TicketThread interface.
      const { data, error } = await supabase
        .from('ticket_threads')
        .select('id, ticket_id, shop_id, customer_id, created_at, updated_at')
        .eq('ticket_id', ticketId)
        .maybeSingle();

      if (error) throw error;
      return data as TicketThread | null;
    },
    enabled: !!ticketId,
  });

  const threadId = threadQuery.data?.id;

  // Fetch events for thread with author names
  // Priority logic (Required Fix #2): query by threadId if available, else by ticketId
  const eventsQuery = useQuery({
    queryKey: ['thread-events', threadId, ticketId],
    queryFn: async () => {
      // Priority: use threadId if available, else fall back to ticketId.
      // OCIS-R66-FIX (MED-7): secondary sort by `id` guarantees
      // deterministic ordering when multiple events share a `created_at`
      // (batched inserts from a single operation — e.g. timer_stopped +
      // ticket_completed fired in the same tick both get the same
      // `now()` server default). Without the tiebreaker, pagination and
      // realtime replays can flip order between page loads.
      // R125-M-7: explicit column list matches ThreadEvent interface.
      let query = supabase
        .from('ticket_thread_events')
        .select('id, thread_id, ticket_id, inbox_thread_id, shop_id, event_type, event_source, channel, visibility, content, payload, author_user_id, author_role, customer_id, is_pinned, parent_event_id, created_at')
        .order('created_at', { ascending: true })
        .order('id', { ascending: true });

      if (threadId) {
        query = query.eq('thread_id', threadId);
      } else if (ticketId) {
        query = query.eq('ticket_id', ticketId);
      } else {
        return [];
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      const events = data || [];
      
      // Fetch author names from profiles for events with author_user_id
      const authorIds = [...new Set(events.map((e: any) => e.author_user_id).filter(Boolean))];
      let authorMap: Record<string, string> = {};
      
      if (authorIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, display_name')
          .in('id', authorIds);
        
        if (profiles) {
          authorMap = Object.fromEntries(profiles.map((p: any) => [p.id, p.display_name || '']));
        }
      }
      
      return events.map((event: any) => ({
        ...event,
        author_name: authorMap[event.author_user_id] || null,
      })) as ThreadEvent[];
    },
    enabled: !!threadId || !!ticketId,
  });

  // Fetch AI summary
  const summaryQuery = useQuery({
    queryKey: ['thread-summary', threadId],
    queryFn: async () => {
      if (!threadId) return null;
      
      // R125-M-8: explicit column list matches AISummary interface.
      const { data, error } = await supabase
        .from('ticket_ai_summaries')
        .select('id, thread_id, summary_text, summary_in_progress, model_used, created_at, updated_at')
        .eq('thread_id', threadId)
        .maybeSingle();

      if (error) throw error;
      return data as AISummary | null;
    },
    enabled: !!threadId,
  });

  const refetch = () => {
    threadQuery.refetch();
    eventsQuery.refetch();
    summaryQuery.refetch();
  };

  return {
    thread: threadQuery.data || null,
    events: eventsQuery.data || [],
    summary: summaryQuery.data || null,
    isLoading: threadQuery.isLoading || eventsQuery.isLoading || (!!threadId && summaryQuery.isLoading),
    error: threadQuery.error || eventsQuery.error || summaryQuery.error,
    refetch,
  };
}
